import { ElementObserverProps } from "../../types.js";
import { DELAY } from "../../const.js";
import { debounce } from "../debounce.js";
import { ElementCallbackHandler, ElementHandlers } from "./handlers.js";

export class ElementObserver<T extends Element> {
  onChange: ElementObserverProps<T>["onChange"];
  onClick: ElementObserverProps<T>["onClick"];
  onMove: ElementObserverProps<T>["onMove"];
  onLeave: ElementObserverProps<T>["onLeave"];
  waitChange: number;
  waitClick: number;
  waitMove: number;
  waitLeave: number;
  changehandler: ElementCallbackHandler<T, "onChange">;
  clickHandler: ElementCallbackHandler<T, "onClick">;
  moveHandler: ElementCallbackHandler<T, "onMove">;
  leaveHandler: ElementCallbackHandler<T, "onLeave">;
  element?: T;
  resizeObserver?: ResizeObserver;
  clickListener?: (event: MouseEvent) => void;
  mouseMoveListener?: (event: MouseEvent) => void;

  constructor(args: ElementObserverProps<T> = {}) {
    const {
      onChange,
      onClick,
      onMove,
      onLeave,
      waitChange = DELAY,
      waitClick = DELAY,
      waitMove = DELAY,
      waitLeave = DELAY,
      element,
    } = args;
    this.onChange = onChange;
    this.onClick = onClick;
    this.onMove = onMove;
    this.onLeave = onLeave;
    this.waitChange = waitChange;
    this.waitClick = waitClick;
    this.waitMove = waitMove;
    this.waitLeave = waitLeave;

    this.changehandler = debounce(onChange, waitChange);
    this.clickHandler = debounce(onClick, waitClick);
    this.moveHandler = debounce(onMove, waitMove);
    this.leaveHandler = debounce(onLeave, waitLeave);

    this.setElement(element);
  }

  setElement(element?: T) {
    this.removeListeners();
    this.element = element;
    this.addListeners();
    return this;
  }

  addListeners() {
    if (this.element) {
      this.resizeObserver = ElementHandlers.addResizeObserver(
        this.element,
        this.changehandler
      );
      if (this.clickHandler.hasCallback) {
        this.clickListener = ElementHandlers.addClickListener(
          this.element,
          this.clickHandler
        );
      }
      if (this.moveHandler.hasCallback || this.leaveHandler.hasCallback) {
        this.clickListener = ElementHandlers.addMouseMoveListener(
          this.element,
          this.moveHandler,
          this.leaveHandler
        );
      }
    }
    return this;
  }

  removeListeners() {
    if (this.element) {
      if (this.resizeObserver) {
        ElementHandlers.removeResizeObserver(this.element, this.resizeObserver);
        this.resizeObserver = undefined;
      }
      if (this.clickListener) {
        ElementHandlers.removeClickListener(this.clickListener);
        this.clickListener = undefined;
      }
      if (this.mouseMoveListener) {
        ElementHandlers.removeMouseMoveListener(this.mouseMoveListener);
        this.mouseMoveListener = undefined;
      }
    }
    return this;
  }
}
